/**
 * The application's own keys: `g` then a letter to go somewhere, `?` for the list.
 *
 * ONE LAYER, IN THE SHELL, ON EVERY PAGE. These began as the dashboard's, and on the dashboard
 * they were the way out of the one page an operator leaves first — somebody working the review
 * queue who wanted arrears still had to reach for the rail. DESIGN_SYSTEM §9's grammar is "`g`
 * then a letter to navigate", not "on the overview", so the keys belong to the application.
 *
 * PURE, like `queueKeys.ts`: a shortcut that stops working is invisible to a typecheck, so the
 * decision lives where a test can reach it and `ShortcutKeys` only wires it to the document.
 */

/**
 * Where `g` goes.
 *
 * PLACES ARE THE RAIL, in the rail's order — the first letter wherever it is free, so most of
 * them are guessable without the list. WORK is the handful of queues an operator drives through
 * every day and that sit a tab below a rail item; they were the dashboard's keys, kept so the
 * cards there can go on announcing them. ACCOUNT is what the account menu holds rather than the
 * rail — Settings.
 *
 * A test pins the places against `NAV`, so a section added to the rail without a letter fails
 * rather than quietly being the one place the keyboard cannot reach.
 */
export const GO_SHORTCUTS = [
  { letter: 'd', to: '/', does: 'Dashboard', group: 'places' },
  { letter: 'r', to: '/reconcile', does: 'Reconcile', group: 'places' },
  { letter: 'f', to: '/financial', does: 'Financial', group: 'places' },
  { letter: 'o', to: '/owners', does: 'Owners', group: 'places' },
  { letter: 'p', to: '/properties', does: 'Properties', group: 'places' },
  { letter: 't', to: '/tenancies', does: 'Tenancies', group: 'places' },
  // pEople: `p` is Properties, which is the record list somebody reaches for more often.
  { letter: 'e', to: '/people', does: 'People', group: 'places' },
  // bIlls: `b` is Bonds, which had it first on the dashboard.
  { letter: 'i', to: '/bills', does: 'Bills', group: 'places' },
  { letter: 'm', to: '/maintenance', does: 'Maintenance', group: 'places' },
  { letter: 'c', to: '/communications/inbox', does: 'Communications', group: 'places' },
  // aUtomations: `a` is Arrears, a page somebody visits daily rather than once a quarter.
  { letter: 'u', to: '/automations', does: 'Automations', group: 'places' },
  // `r` is Reconcile; reports are where things are eXported.
  { letter: 'x', to: '/reports', does: 'Reports', group: 'places' },

  { letter: 'q', to: '/reconcile/queue', does: 'Review queue', group: 'work' },
  { letter: 's', to: '/reconcile/suspense', does: 'Suspense', group: 'work' },
  { letter: 'a', to: '/tenancies/arrears', does: 'Arrears', group: 'work' },
  { letter: 'b', to: '/tenancies/bonds', does: 'Bonds', group: 'work' },
  // `d` is the Dashboard; a payment run is money Withdrawn from trust.
  { letter: 'w', to: '/financial/disbursements', does: 'Disbursements', group: 'work' },
  // `e` is People; the event log is eVents.
  { letter: 'v', to: '/reports/events', does: 'Event log', group: 'work' },

  /*
   * `,` rather than `s`, which is Suspense — a queue worked daily, where Settings is visited a few
   * times a month. Comma is the settings key wherever there is a convention for one: Cmd+, opens
   * preferences in nearly every Mac app.
   */
  { letter: ',', to: '/settings', does: 'Settings', group: 'account' },
] as const;

export type GoDestination = (typeof GO_SHORTCUTS)[number]['to'];

/** `aria-keyshortcuts` for a destination — `G Q` — so a control can say which keys open it. */
export function keyShortcutFor(to: string): string | undefined {
  const entry = GO_SHORTCUTS.find((shortcut) => shortcut.to === to);
  return entry ? `G ${entry.letter.toUpperCase()}` : undefined;
}

export interface GlobalKeyEvent {
  key: string;
  ctrlKey: boolean;
  metaKey: boolean;
  altKey: boolean;
  repeat: boolean;
  /** Whether the keystroke landed in something the user is typing into. */
  isEditable: boolean;
  /**
   * Whether it landed inside a modal dialog other than the shortcuts list itself.
   *
   * A modal is a question being asked. `g p` from a focused button in "Choose a tenancy" would
   * navigate out from under it, and `?` would stack a second modal on the first.
   */
  inDialog: boolean;
  /** Whether `g` was the previous key, and recently enough to still count. */
  armed: boolean;
  /** The browser's "Single-key shortcuts" setting — see ./singleKeys. Off, this layer claims nothing. */
  singleKeys: boolean;
}

export type GlobalIntent =
  | { kind: 'arm' }
  | { kind: 'go'; to: GoDestination }
  /**
   * The key after `g` that is not a destination: forget the `g` AND keep the key from the page.
   *
   * That is what makes this layer safe to put over pages with keys of their own. The review
   * queue's `t` opens the tenancy picker and its `u` undoes a match; `g u` must go to Automations
   * and nothing else, and `g z` must not fall through to a page that one day claims `z`. Once `g`
   * is down, the next key is this layer's whatever it turns out to be.
   */
  | { kind: 'cancel' }
  | { kind: 'toggleHelp' }
  | { kind: 'ignore' };

/** Pressed on their own while reaching for a letter — Shift for `?`, mostly. Never the chord's key. */
const MODIFIERS = new Set(['Shift', 'Control', 'Alt', 'Meta', 'CapsLock', 'AltGraph']);

/**
 * What a keystroke means to the application, before any page sees it.
 *
 * NOTHING WHILE TYPING: every key here is a plain letter, and `g` in "Grey Lynn" must stay a `g`.
 *
 * NOTHING WITH A MODIFIER. Ctrl+R, Cmd+L and Ctrl+P belong to the browser, and swallowing them to
 * mean "reconcile", "ledger" or "properties" would be the fastest way to make the keys hated.
 * Shift is allowed through, for `?` and for a capital letter typed with Caps Lock on.
 */
export function globalKeyIntent(event: GlobalKeyEvent): GlobalIntent {
  if (!event.singleKeys) return { kind: 'ignore' };
  if (event.isEditable || event.inDialog) return { kind: 'ignore' };
  if (event.ctrlKey || event.metaKey || event.altKey) return { kind: 'ignore' };
  if (MODIFIERS.has(event.key)) return { kind: 'ignore' };

  if (event.armed) {
    // A held letter after `g` has already navigated; its repeats are still not the page's.
    if (event.repeat) return { kind: 'cancel' };
    const letter = event.key.toLowerCase();
    const target = GO_SHORTCUTS.find((shortcut) => shortcut.letter === letter);
    return target ? { kind: 'go', to: target.to } : { kind: 'cancel' };
  }

  // A held `g` arms once; a held `?` would flicker the list open and shut.
  if (event.repeat) return { kind: 'ignore' };
  if (event.key === 'g' || event.key === 'G') return { kind: 'arm' };
  if (event.key === '?') return { kind: 'toggleHelp' };
  return { kind: 'ignore' };
}

/**
 * Marks a dialog the application's keys still work inside — the help drawer, which is a side
 * panel rather than a question, and the shortcuts list itself. Every other dialog is left alone.
 */
export const GLOBAL_KEYS_PASS = 'data-global-keys';

/** How long a `g` waits for its letter. Long enough to look down at the keys, short enough to forget. */
export const CHORD_TIMEOUT_MS = 1500;
